import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { DRILL_TAGS, DRILL_CATEGORIES, DRILL_DIFFICULTIES } from '@/lib/drillTags';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Clock, BookOpen, Plus, Pencil, Trash2, ChevronRight, Star, Play } from 'lucide-react';
import DrillInfoModal, { toWatchUrl } from './DrillInfoModal';

const EMPTY_DRILL = {
  name: '', description: '', duration_minutes: 10, tags: [],
  category: 'skill_work', difficulty: 'intermediate', video_url: '',
};

function DrillFormModal({ open, onOpenChange, drill, onSave }) {
  const [form, setForm] = useState(EMPTY_DRILL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(drill ? { ...EMPTY_DRILL, ...drill } : { ...EMPTY_DRILL });
  }, [open, drill]);

  const isEdit = !!drill?.drill_id;

  const toggleTag = (tag) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Drill name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/drills/${drill.drill_id}`, form);
        toast.success('Drill updated');
      } else {
        await api.post('/drills', { ...form, is_public: false });
        toast.success('Drill created');
      }
      onSave();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save drill');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {isEdit ? 'Edit Drill' : 'Create Custom Drill'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Name *</label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. 3-on-2 Fast Break" data-testid="drill-form-name" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Drill instructions..." rows={3} data-testid="drill-form-description" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Duration (min)</label>
              <Input type="number" min={1} max={120} value={form.duration_minutes}
                onChange={e => setForm(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 10 }))}
                data-testid="drill-form-duration" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Category</label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="drill-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRILL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Difficulty</label>
              <Select value={form.difficulty} onValueChange={v => setForm(p => ({ ...p, difficulty: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="drill-form-difficulty"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DRILL_DIFFICULTIES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Video URL (YouTube)</label>
            <Input value={form.video_url} onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))}
              placeholder="https://youtube.com/watch?v=..." data-testid="drill-form-video" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Tags</label>
            <div className="flex flex-wrap gap-1.5">
              {DRILL_TAGS.map(tag => (
                <button type="button" key={tag} onClick={() => toggleTag(tag)}
                  className={`text-[11px] px-2 py-1 rounded-full font-bold transition-colors ${
                    form.tags.includes(tag)
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                  data-testid={`drill-tag-${tag.replace(/\s+/g, '-').toLowerCase()}`}>
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-sm"
              data-testid="drill-form-submit">
              {saving ? 'Saving...' : isEdit ? 'Update Drill' : 'Create Drill'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="font-bold uppercase tracking-wider text-sm">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DrillCard({ drill, isOwner, onEdit, onDelete, isFavorite, onToggleFavorite, onOpenVideo }) {
  const catLabel = (val) => DRILL_CATEGORIES.find(c => c.value === val)?.label || val;
  const hasVideo = !!toWatchUrl(drill.video_url);

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`drill-card-${drill.drill_id}`}>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <button
            // Always open the info modal on tap — video presence used to
            // gate this, leaving video-less drills with no way to view
            // their description/duration/tags (build 211 hotfix).
            onClick={() => onOpenVideo(drill)}
            className="text-left flex-1 min-w-0"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid={`drill-name-${drill.drill_id}`}
          >
            <h3 className="font-bold text-slate-900 text-sm leading-tight truncate">{drill.name}</h3>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            {hasVideo && (
              <button
                onClick={() => onOpenVideo(drill)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-[.06em] transition-colors hover:opacity-90"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#F1F5F9', color: '#13314b', border: '1px solid #DDE5EC' }}
                data-testid={`drill-watch-${drill.drill_id}`}
              >
                <Play className="w-3 h-3" fill="currentColor" /> Watch
              </button>
            )}
            {!isOwner && onToggleFavorite && (
              <button
                onClick={() => onToggleFavorite(drill.drill_id)}
                className={`p-1 transition-colors ${isFavorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                style={{ background: 'none', border: 'none' }}
                data-testid={`drill-favorite-${drill.drill_id}`}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
            )}
            {isOwner && (
              <>
                <button onClick={() => onEdit(drill)} className="p-1 text-slate-400 hover:text-slate-700 transition-colors"
                  style={{ background: 'none', border: 'none' }} data-testid={`drill-edit-${drill.drill_id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => onDelete(drill.drill_id)} className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                  style={{ background: 'none', border: 'none' }} data-testid={`drill-delete-${drill.drill_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
        {drill.description && (
          <p className="text-xs text-slate-500 mb-1.5 line-clamp-2">{drill.description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{drill.duration_minutes}m</span>
          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{catLabel(drill.category)}</span>
          <span className="capitalize">{drill.difficulty}</span>
        </div>
        {drill.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {drill.tags.map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DrillLibrary({ user }) {
  const [publicDrills, setPublicDrills] = useState([]);
  const [myDrills, setMyDrills] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [totalPublic, setTotalPublic] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editDrill, setEditDrill] = useState(null);
  const [videoDrill, setVideoDrill] = useState(null);
  const [customExpanded, setCustomExpanded] = useState(true);
  const [publicExpanded, setPublicExpanded] = useState(true);

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await api.get('/drills/favorites');
      setFavorites(res.data.favorite_drill_ids || []);
    } catch { /* ignore */ }
  }, []);

  const fetchDrills = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('visibility', 'public');
      if (search) params.append('search', search);
      if (tagFilter) params.append('tag', tagFilter);
      if (catFilter) params.append('category', catFilter);
      const pubRes = await api.get(`/drills?${params}`);
      setPublicDrills(pubRes.data.drills);
      setTotalPublic(pubRes.data.total);

      const myParams = new URLSearchParams();
      myParams.append('visibility', 'private');
      if (search) myParams.append('search', search);
      if (tagFilter) myParams.append('tag', tagFilter);
      if (catFilter) myParams.append('category', catFilter);
      const myRes = await api.get(`/drills?${myParams}`);
      setMyDrills(myRes.data.drills);
    } catch { toast.error('Failed to load drills'); }
    finally { setLoading(false); }
  }, [search, tagFilter, catFilter]);

  useEffect(() => {
    fetchDrills();
    fetchFavorites();
  }, [fetchDrills, fetchFavorites]);

  const toggleFavorite = async (drillId) => {
    try {
      const res = await api.post(`/drills/${drillId}/favorite`);
      setFavorites(res.data.favorite_drill_ids || []);
      toast.success(res.data.is_favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch {
      toast.error('Failed to update favorite');
    }
  };

  const handleDelete = async (drillId) => {
    if (!window.confirm('Delete this drill?')) return;
    try {
      await api.delete(`/drills/${drillId}`);
      toast.success('Drill deleted');
      fetchDrills();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleEdit = (drill) => { setEditDrill(drill); setShowForm(true); };
  const handleCreate = () => { setEditDrill(null); setShowForm(true); };

  /* Apply favorites filter to the public list (favorites are only on public drills). */
  const visiblePublic = useMemo(() => (
    favoritesOnly ? publicDrills.filter(d => favorites.includes(d.drill_id)) : publicDrills
  ), [publicDrills, favoritesOnly, favorites]);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-testid="drill-library-title">
            Drill Library
          </h2>
          <p className="text-sm text-slate-500">{totalPublic} public drill{totalPublic !== 1 ? 's' : ''} · {myDrills.length} custom · {favorites.length} favorited</p>
        </div>
        <Button onClick={handleCreate} className="rounded-full bg-[#F5A623] hover:bg-[#E8961A] text-[#0D1B26] font-semibold uppercase tracking-[.07em] text-[15px] gap-1.5 px-5"
          data-testid="create-drill-button"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          <Plus className="w-4 h-4" /> New Drill
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-3 flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[160px]">
          <Input placeholder="Search drills..." value={search} onChange={e => setSearch(e.target.value)}
            className="h-9 text-sm" data-testid="drill-search-input" />
        </div>
        <Select value={tagFilter || "_all"} onValueChange={v => setTagFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-[160px] h-9 text-sm"><SelectValue placeholder="All Tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Tags</SelectItem>
            {DRILL_TAGS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter || "_all"} onValueChange={v => setCatFilter(v === '_all' ? '' : v)}>
          <SelectTrigger className="w-[150px] h-9 text-sm"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Categories</SelectItem>
            {DRILL_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Favorites toggle */}
        <button
          onClick={() => setFavoritesOnly(v => !v)}
          data-testid="drill-favorites-toggle"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all active:scale-[.97]"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            background: favoritesOnly ? '#F5A623' : '#fff',
            color: favoritesOnly ? '#0D1B26' : '#13314b',
            border: `1.5px solid ${favoritesOnly ? '#F5A623' : '#DDE5EC'}`,
          }}
        >
          <Star className="w-3.5 h-3.5" fill={favoritesOnly ? 'currentColor' : 'none'} />
          Favorites {favoritesOnly ? `(${favorites.length})` : ''}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading drills...</div>
      ) : (
        <>
          {/* My Custom Drills */}
          {!favoritesOnly && myDrills.length > 0 && (
            <div className="mb-5">
              <button
                onClick={() => setCustomExpanded(!customExpanded)}
                className="flex items-center gap-2 w-full text-left mb-2 group"
                data-testid="my-drills-header"
              >
                <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${customExpanded ? 'rotate-90' : ''}`} />
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  My Custom Drills ({myDrills.length})
                </h3>
              </button>
              {customExpanded && (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {myDrills.map(drill => (
                    <DrillCard key={drill.drill_id} drill={drill} isOwner={true}
                      onEdit={handleEdit} onDelete={handleDelete} onOpenVideo={setVideoDrill} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Public Drills */}
          <button
            onClick={() => setPublicExpanded(!publicExpanded)}
            className="flex items-center gap-2 w-full text-left mb-2 group"
            data-testid="public-drills-header"
          >
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${publicExpanded ? 'rotate-90' : ''}`} />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
              {favoritesOnly ? `Favorited Drills (${visiblePublic.length})` : `Public Drills (${totalPublic})`}
            </h3>
          </button>
          {publicExpanded && (
            <>
              {visiblePublic.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                  <p className="text-slate-500 font-medium">
                    {favoritesOnly ? 'No favorited drills yet — tap the star on any drill to add it.' : 'No public drills match your filters'}
                  </p>
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {visiblePublic.map(drill => (
                    <DrillCard
                      key={drill.drill_id}
                      drill={drill}
                      isOwner={false}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      isFavorite={favorites.includes(drill.drill_id)}
                      onToggleFavorite={toggleFavorite}
                      onOpenVideo={setVideoDrill}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      <DrillFormModal open={showForm} onOpenChange={setShowForm} drill={editDrill} onSave={fetchDrills} />
      <DrillInfoModal drill={videoDrill} onOpenChange={(v) => { if (!v) setVideoDrill(null); }} />
    </div>
  );
}
